/**
 * Gated client logger (pentest follow-up: UI console logging).
 *
 * - error(): always forwards to console.error
 * - warn/info/debug/log: suppressed unless enabled via:
 *   - NEXT_PUBLIC_CLIENT_LOG_LEVEL=error|warn|info|debug (default: error)
 *   - or NEXT_PUBLIC_CLIENT_LOG_VERBOSE=true (enables all levels)
 *
 * Prefer logger.error for anything that must reach the console.
 * Pair with installClientConsoleGate() so legacy console.log/warn/info/debug
 * are suppressed under the same rules.
 */

export type ClientLogLevel = 'error' | 'warn' | 'info' | 'debug'

const LEVEL_ORDER: Record<ClientLogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
}

function resolveConfiguredLevel(): ClientLogLevel {
  if (typeof process === 'undefined') return 'error'
  const verbose = process.env.NEXT_PUBLIC_CLIENT_LOG_VERBOSE?.trim().toLowerCase()
  if (verbose === '1' || verbose === 'true' || verbose === 'yes') {
    return 'debug'
  }
  const raw = process.env.NEXT_PUBLIC_CLIENT_LOG_LEVEL?.trim().toLowerCase()
  if (raw === 'warn' || raw === 'info' || raw === 'debug' || raw === 'error') {
    return raw
  }
  // Default: errors only (opt in to more with LEVEL or VERBOSE)
  return 'error'
}

function shouldEmit(level: ClientLogLevel): boolean {
  const configured = resolveConfiguredLevel()
  return LEVEL_ORDER[level] <= LEVEL_ORDER[configured]
}

export const logger = {
  error: (...args: unknown[]) => {
    console.error(...args)
  },
  warn: (...args: unknown[]) => {
    if (shouldEmit('warn')) {
      console.warn(...args)
    }
  },
  info: (...args: unknown[]) => {
    if (shouldEmit('info')) {
      console.info(...args)
    }
  },
  log: (...args: unknown[]) => {
    // Treat log as info-level verbosity
    if (shouldEmit('info')) {
      console.log(...args)
    }
  },
  debug: (...args: unknown[]) => {
    if (shouldEmit('debug')) {
      console.debug(...args)
    }
  },
}
